<template>
    <div class="alarm-center">
      <!-- 左侧面板 -->
      <div class="alarm-left-panel-wrapper">
        <div class="alarm-left-panel">
        <!-- 告警事件趋势 -->
        <div class="card section-trend">
          <div class="section-header">
            <div class="section-title">告警事件趋势</div>
            <button class="btn btn-sm" @click="showDetails">详情</button>
          </div>
          <div class="trend-controls">
            <button class="time-btn" :class="{ active: trendTimeRange === '7days' }" @click="trendTimeRange = '7days'">近7天</button>
            <button class="time-btn" :class="{ active: trendTimeRange === 'halfyear' }" @click="trendTimeRange = 'halfyear'">近半年</button>
          </div>
          <div class="trend-chart">
            <div ref="trendChartRef" class="chart-container"></div>
          </div>
        </div>

        <!-- 告警区域排行 -->
        <div class="card section-ranking">
          <div class="section-header">
            <div class="section-title">告警区域排行</div>
            <button class="btn btn-sm" @click="showDetails">详情</button>
          </div>
          <div class="ranking-controls">
            <button class="time-btn" :class="{ active: rankingTimeRange === '7days' }" @click="rankingTimeRange = '7days'">近7天</button>
            <button class="time-btn" :class="{ active: rankingTimeRange === 'halfyear' }" @click="rankingTimeRange = 'halfyear'">近半年</button>
          </div>
          <div class="ranking-chart">
            <div ref="rankingChartRef" class="chart-container"></div>
          </div>
        </div>

        </div>
      </div>

      <!-- 中央地图 -->
      <div class="alarm-center-panel">
        <div id="alarm-amap-container" class="amap-container">
          <!-- 实时探测加载（地图右下角） -->
          <div class="radar-scanner">
            <canvas ref="radarCanvasRef" class="radar-canvas"></canvas>
          </div>
          <!-- 今日告警事件（地图右上角） -->
          <div class="map-today-alarms">
            <div class="today-alarms-header">
              <div class="header-icon">🔔</div>
              <span class="header-title">今日告警事件</span>
            </div>
            <div class="today-alarms-list">
              <div
                v-for="alarm in todayAlarms"
                :key="alarm.type"
                class="today-alarm-item"
                :class="{ active: selectedAlarmType === alarm.type }"
                @click="selectAlarmType(alarm.type)"
              >
                <div class="alarm-icon">{{ getAlarmTypeIcon(alarm.type) }}</div>
                <div class="alarm-type">{{ alarm.type }}</div>
                <div class="alarm-count">{{ alarm.count }} 件</div>
              </div>
            </div>
          </div>
          <!-- 告警事件列表（地图正下方，悬浮） -->
          <div class="map-alarm-list-wrapper">
            <!-- 展开按钮（当列表隐藏时显示） -->
            <transition name="fade">
              <button
                v-if="!alarmListVisible"
                class="alarm-list-toggle-btn"
                type="button"
                @click="alarmListVisible = true"
              >
                <span class="toggle-text">告警事件列表</span>
                <span class="toggle-icon-up">▲</span>
              </button>
            </transition>
            <!-- 告警事件列表 -->
            <transition name="slide-up">
              <div v-show="alarmListVisible" class="map-alarm-list">
                <div class="alarm-list-header">
                  <div class="section-title">告警事件列表</div>
                  <button
                    class="toggle-btn"
                    type="button"
                    @click.stop="alarmListVisible = false"
                  >
                    <span class="toggle-icon-down">▼</span>
                  </button>
                </div>
                <div class="alarm-list-scroll">
                  <div class="alarm-table">
                    <div class="table-header">
                      <div class="th">设备编号</div>
                      <div class="th">设备名称</div>
                      <div class="th">告警类型</div>
                      <div class="th">告警时间</div>
                      <div class="th">告警地点</div>
                      <div class="th">处置状态</div>
                      <div class="th">操作</div>
                    </div>
                    <div
                      v-for="alarm in alarmList"
                      :key="`${alarm.deviceId}-${alarm.time}`"
                      class="table-row"
                      :class="{ 'new-alarm': alarm.isNew }"
                    >
                      <div class="td">{{ alarm.deviceId }}</div>
                      <div class="td">{{ alarm.deviceName }}</div>
                      <div class="td">{{ alarm.type }}</div>
                      <div class="td">{{ alarm.time }}</div>
                      <div class="td">{{ alarm.location }}</div>
                      <div class="td">
                        <span class="status-badge" :class="alarm.statusClass">{{ alarm.status }}</span>
                      </div>
                      <div class="td">
                        <button class="detail-btn" @click="viewAlarmDetail(alarm)">详情</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- 告警点位悬浮层 -->
        <div
          v-if="alarmPopupVisible && alarmPopup"
          class="alarm-popup"
          :style="{ left: `${alarmPopupPos.x}px`, top: `${alarmPopupPos.y}px` }"
          @mousedown.stop
        >
          <button class="popup-close" type="button" @click="closeAlarmPopup">×</button>
          <div class="popup-body">
            <div class="popup-content">
              <div v-if="alarmPopup.title" class="popup-head">{{ alarmPopup.title }}</div>
              <div v-for="(row, idx) in alarmPopup.rows" :key="idx" class="popup-row">
                <span v-for="(item, j) in row" :key="j" class="kv"><b>{{ item.k }}：</b>{{ item.v }}</span>
              </div>
              <div v-if="alarmPopup.location" class="popup-address"><b>告警地点：</b>{{ alarmPopup.location }}</div>
              <div v-if="alarmPopup.status" class="popup-status">
                <b>处置状态：</b>
                <span class="status-badge" :class="alarmPopup.statusClass">{{ alarmPopup.status }}</span>
              </div>
              <button class="popup-action-btn" @click="viewAlarmDetail">详情</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧面板 -->
      <div class="alarm-right-panel-wrapper">
        <div class="alarm-right-panel">
          <!-- 本月工单状态 -->
          <div class="card section-workorder">
            <div class="section-header">
              <div class="section-title">本月工单状态</div>
              <button class="btn btn-sm" @click="showDetails">详情</button>
            </div>
            <div class="workorder-content">
              <div class="workorder-left">
                <div class="workorder-stat">
                  <div class="stat-label">已转化工单数</div>
                  <div class="stat-value">1234 <span class="stat-unit">件</span></div>
                  <div class="stat-badge">占比 10%</div>
                </div>
                <div class="workorder-stat">
                  <div class="stat-label">已办结工单数</div>
                  <div class="stat-value">1234 <span class="stat-unit">件</span></div>
                  <div class="stat-badge">占比 10%</div>
                </div>
              </div>
              <div class="workorder-right">
                <div class="status-list">
                  <div class="status-item">
                    <span class="status-label">处置中:</span>
                    <span class="status-value cyan">123 件</span>
                  </div>
                  <div class="status-item">
                    <span class="status-label">已处置:</span>
                    <span class="status-value red">456 件</span>
                  </div>
                  <div class="status-item">
                    <span class="status-label">已关闭:</span>
                    <span class="status-value cyan">1234 件</span>
                  </div>
                </div>
                <div class="satisfaction">
                  <div class="satisfaction-label">工单满意度统计:</div>
                  <div class="satisfaction-bars">
                    <div class="bar-item">
                      <span class="bar-label">满意:</span>
                      <div class="bar-wrapper">
                        <div class="bar-fill" style="width: 70%; background: #00d4ff;"></div>
                      </div>
                      <span class="bar-value">70%</span>
                    </div>
                    <div class="bar-item">
                      <span class="bar-label">不满意:</span>
                      <div class="bar-wrapper">
                        <div class="bar-fill" style="width: 20%; background: #ff6b6b;"></div>
                      </div>
                      <span class="bar-value">20%</span>
                    </div>
                    <div class="bar-item">
                      <span class="bar-label">未评价:</span>
                      <div class="bar-wrapper">
                        <div class="bar-fill" style="width: 10%; background: #a0b3c8;"></div>
                      </div>
                      <span class="bar-value">10%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <script>
  import { communityStore } from '../stores/community'
  import { getCommunityConfig } from '../utils/communityConfig'
  import * as echarts from 'echarts'

  export default {
    name: 'AlarmCenter',
    data() {
      return {
        communityStore,
        // 图表引用
        trendChart: null,
        rankingChart: null,
        // 雷达扫描器
        radarAnimationFrame: null,
        radarAngle: 0,
        communityCenter: { lon: 120.15, lat: 30.28 },
        communityBounds: { minLon: 120.145, maxLon: 120.155, minLat: 30.275, maxLat: 30.285 },
        // 时间范围
        trendTimeRange: '7days',
        rankingTimeRange: '7days',
        // 告警悬浮层状态
        alarmPopup: null,
        alarmPopupVisible: false,
        alarmPopupPos: { x: -9999, y: -9999 },
        selectedAlarmMarker: null,
        mapMoveHandler: null,
        // 选中的告警类型（用于过滤）
        selectedAlarmType: null,
        // 告警事件列表显示/隐藏
        alarmListVisible: true,
        // 实时更新定时器
        alarmUpdateTimer: null,
        alarmListMaxCount: 50,
        // 高德地图
        map: null,
        boundaryPolyline: null,
        alarmMarkers: [],
        // 今日告警事件
        todayAlarms: [
          { type: '高空抛物', count: 18 },
          { type: '垃圾满溢', count: 18 },
          { type: '设备告警', count: 18 },
          { type: '车辆告警', count: 18 },
          { type: '人员告警', count: 18 }
        ],
        // 告警事件列表
        alarmList: [
          {
            deviceId: 'GP-220701',
            deviceName: '2栋南侧高空抛物摄像机',
            type: '垃圾满溢',
            time: '2022/07/01 07:12:35',
            location: '青云锦绣小区2栋南侧区域',
            status: '未派发',
            statusClass: 'unassigned',
            lon: 120.148,
            lat: 30.279
          },
          {
            deviceId: 'GP-220702',
            deviceName: '3栋东侧高空抛物摄像机',
            type: '高空抛物',
            time: '2022/07/01 08:25:10',
            location: '青云锦绣小区3栋东侧区域',
            status: '已取消',
            statusClass: 'cancelled',
            lon: 120.149,
            lat: 30.280
          },
          {
            deviceId: 'GP-220703',
            deviceName: '1栋北侧门禁设备',
            type: '设备告警',
            time: '2022/07/01 09:15:42',
            location: '青云锦绣小区1栋北侧区域',
            status: '已派发',
            statusClass: 'assigned',
            lon: 120.147,
            lat: 30.278
          },
          {
            deviceId: 'GP-220704',
            deviceName: '小区入口车辆识别系统',
            type: '车辆告警',
            time: '2022/07/01 10:30:20',
            location: '青云锦绣小区主入口',
            status: '未派发',
            statusClass: 'unassigned',
            lon: 120.151,
            lat: 30.281
          },
          {
            deviceId: 'GP-220705',
            deviceName: '5栋中心区域人员识别系统',
            type: '人员告警',
            time: '2022/07/01 11:45:55',
            location: '青云锦绣小区5栋中心区域',
            status: '已派发',
            statusClass: 'assigned',
            lon: 120.150,
            lat: 30.282
          }
        ]
      }
    },
    watch: {
      trendTimeRange() {
        if (this.trendChart) this.initTrendChart()
      },
      rankingTimeRange() {
        if (this.rankingChart) this.initRankingChart()
      },
      'communityStore.currentCommunity'() {
        this.loadCommunityData()
      },
      selectedAlarmType() {
        // 当告警类型过滤改变时，过滤标记并重新绘制雷达
        this.filterAlarmMarkers()
      }
    },
    mounted() {
      // 初始化高德地图
      this.$nextTick(() => {
        setTimeout(() => {
          this.initAmap()
        }, 200)
      })

      // 监听窗口大小变化
      window.addEventListener('resize', this.handleResize)

      // 初始化图表和雷达扫描器
      setTimeout(() => {
        this.loadCommunityData()
        this.initTrendChart()
        this.initRankingChart()
        this.initRadarScanner()
        // 默认选中第一个今日告警事件项
        if (this.todayAlarms.length > 0) {
          this.selectedAlarmType = this.todayAlarms[0].type
          this.filterAlarmMarkers()
        }
        // 启动实时告警更新
        this.startAlarmUpdates()
      }, 400)
    },
    beforeDestroy() {
      // 停止实时更新
      this.stopAlarmUpdates()

      // 移除事件监听
      window.removeEventListener('resize', this.handleResize)

      if (this.trendChart) {
        this.trendChart.dispose()
        this.trendChart = null
      }
      if (this.rankingChart) {
        this.rankingChart.dispose()
        this.rankingChart = null
      }
      if (this.radarAnimationFrame) {
        cancelAnimationFrame(this.radarAnimationFrame)
        this.radarAnimationFrame = null
      }

      // 销毁高德地图
      if (this.map) {
        // 清除所有标记
        this.alarmMarkers.forEach(marker => {
          this.map.remove(marker)
        })
        if (this.boundaryPolyline) {
          this.map.remove(this.boundaryPolyline)
        }
        if (this.mapMoveHandler) {
          this.map.off('moveend', this.mapMoveHandler)
          this.map.off('zoomend', this.mapMoveHandler)
        }
        this.map.destroy()
        this.map = null
      }
    },
    methods: {
      handleResize() {
        if (this.trendChart) this.trendChart.resize()
        if (this.rankingChart) this.rankingChart.resize()
        if (this.map) {
          this.map.resize()
        }
      },
      selectAlarmType(type) {
        // 点击告警类型，过滤地图上的点位
        if (this.selectedAlarmType === type) {
          // 如果已经选中，再次点击则取消选择（显示全部）
          this.selectedAlarmType = null
        } else {
          this.selectedAlarmType = type
        }
        this.filterAlarmEntities()
      },
      filterAlarmMarkers() {
        // 根据选中的类型过滤告警标记
        this.alarmMarkers.forEach((marker) => {
          const alarmData = marker.getExtData() && marker.getExtData().alarmData
          if (!this.selectedAlarmType) {
            // 未选择类型，显示全部
            marker.show()
          } else {
            // 只显示选中类型的告警
            if (alarmData && alarmData.type === this.selectedAlarmType) {
              marker.show()
            } else {
              marker.hide()
            }
          }
        })
      },
      viewAlarmDetail(alarm) {
        // 查看告警详情（可以打开详情模态框）
        if (alarm) {
          // 如果传入了alarm对象，打开弹窗并定位到对应标记
          const marker = this.alarmMarkers.find(m => {
            const extData = m.getExtData()
            return extData && extData.alarmData && 
                   extData.alarmData.deviceId === alarm.deviceId &&
                   extData.alarmData.time === alarm.time
          })
          if (marker) {
            this.selectedAlarmMarker = marker
            this.openAlarmPopup(alarm)
            this.updatePopupPosition()
            // 监听地图移动和缩放，更新弹窗位置
            if (this.mapMoveHandler) {
              this.map.off('moveend', this.mapMoveHandler)
              this.map.off('zoomend', this.mapMoveHandler)
            }
            this.mapMoveHandler = () => {
              this.updatePopupPosition()
            }
            this.map.on('moveend', this.mapMoveHandler)
            this.map.on('zoomend', this.mapMoveHandler)
          }
        }
        console.log('View alarm detail:', alarm)
      },
      generateRandomAlarm() {
        const alarmTypes = ['高空抛物', '垃圾满溢', '设备告警', '车辆告警', '人员告警']
        const statusOptions = [
          { status: '未派发', statusClass: 'unassigned' },
          { status: '已派发', statusClass: 'assigned' },
          { status: '已取消', statusClass: 'cancelled' }
        ]
        const deviceNames = [
          '1栋南侧监控摄像机',
          '2栋北侧门禁设备',
          '3栋东侧高空抛物摄像机',
          '4栋西侧安全监控',
          '5栋中心区域识别系统',
          '小区主入口车辆识别',
          '停车场出入口监控',
          '中心湖区安全监控'
        ]

        const now = new Date()
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const hours = String(now.getHours()).padStart(2, '0')
        const minutes = String(now.getMinutes()).padStart(2, '0')
        const seconds = String(now.getSeconds()).padStart(2, '0')
        const timeStr = `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`

        const cfg = getCommunityConfig(this.communityStore.currentCommunity)
        const boundary = cfg && cfg.boundary || []
        let lon = 120.15
        let lat = 30.28

        if (boundary.length > 0) {
          // 在小区边界内随机生成位置
          const lons = boundary.map((p) => p.longitude)
          const lats = boundary.map((p) => p.latitude)
          const minLon = Math.min(...lons)
          const maxLon = Math.max(...lons)
          const minLat = Math.min(...lats)
          const maxLat = Math.max(...lats)
          lon = minLon + Math.random() * (maxLon - minLon)
          lat = minLat + Math.random() * (maxLat - minLat)
        }

        const type = alarmTypes[Math.floor(Math.random() * alarmTypes.length)]
        const status = statusOptions[Math.floor(Math.random() * statusOptions.length)]
        const deviceName = deviceNames[Math.floor(Math.random() * deviceNames.length)]
        const deviceId = `GP-${now.getFullYear()}${String(Math.floor(Math.random() * 9000) + 1000)}`

        return {
          deviceId,
          deviceName,
          type,
          time: timeStr,
          location: `${cfg && cfg.name || '青云锦绣'}${deviceName.replace(/[0-9]/g, '').replace('监控', '').replace('设备', '').replace('系统', '')}区域`,
          status: status.status,
          statusClass: status.statusClass,
          lon,
          lat,
          isNew: true // 标记为新消息
        }
      },
      addNewAlarm() {
        const newAlarm = this.generateRandomAlarm()

        // 将新消息添加到列表顶部
        this.alarmList.unshift(newAlarm)

        // 限制列表最大数量
        if (this.alarmList.length > this.alarmListMaxCount) {
          this.alarmList = this.alarmList.slice(0, this.alarmListMaxCount)
        }

        // 更新今日告警统计
        const alarmTypeIndex = this.todayAlarms.findIndex(a => a.type === newAlarm.type)
        if (alarmTypeIndex !== -1) {
          this.todayAlarms[alarmTypeIndex].count++
        }

        // 重新加载地图上的告警点位
        if (this.map) {
          this.loadCommunityData()
        }

        // 3秒后移除新消息标识
        setTimeout(() => {
          const index = this.alarmList.findIndex(a =>
            a.deviceId === newAlarm.deviceId &&
            a.time === newAlarm.time
          )
          if (index !== -1) {
            this.$set(this.alarmList[index], 'isNew', false)
          }
        }, 3000)
      },
      startAlarmUpdates() {
        // 每隔5-15秒随机添加一条新告警
        const scheduleNext = () => {
          const delay = 5000 + Math.random() * 10000 // 5-15秒
          this.alarmUpdateTimer = setTimeout(() => {
            this.addNewAlarm()
            scheduleNext()
          }, delay)
        }
        scheduleNext()
      },
      stopAlarmUpdates() {
        if (this.alarmUpdateTimer) {
          clearTimeout(this.alarmUpdateTimer)
          this.alarmUpdateTimer = null
        }
      },
      getAlarmTypeIcon(type) {
        // 根据告警类型返回对应的图标
        const iconMap = {
          '设备告警': '🚪',
          '车辆告警': '🚗',
          '人员告警': '👤',
          '高空抛物': '📹',
          '垃圾满溢': '📹'
        }
        return iconMap[type] || '📹'
      },
      getAlarmIcon() {
        // 告警图标 Canvas（红色警告图标），大小与社区态势设备图标一致
        const canvas = document.createElement('canvas')
        const size = 32
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        
        // 清除画布
        ctx.clearRect(0, 0, size, size)
        
        const centerX = size / 2
        const centerY = size / 2
        
        // 绘制外圈（与社区态势一致）
        ctx.beginPath()
        ctx.arc(centerX, centerY, size / 2 - 2, 0, Math.PI * 2)
        ctx.fillStyle = '#ff4444'
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.stroke()
        
        // 绘制警告三角形（完全居中）
        const triangleWidth = size * 0.45
        const triangleHeight = size * 0.45
        // 计算三角形的三个顶点，确保完全居中
        const topX = centerX
        const topY = centerY - triangleHeight / 2
        const bottomLeftX = centerX - triangleWidth / 2
        const bottomLeftY = centerY + triangleHeight / 2
        const bottomRightX = centerX + triangleWidth / 2
        const bottomRightY = centerY + triangleHeight / 2
        
        ctx.beginPath()
        ctx.moveTo(topX, topY)
        ctx.lineTo(bottomRightX, bottomRightY)
        ctx.lineTo(bottomLeftX, bottomLeftY)
        ctx.closePath()
        
        // 白色填充
        ctx.fillStyle = '#ffffff'
        ctx.fill()
        
        // 绘制感叹号（完全居中）
        ctx.strokeStyle = '#ff4444'
        ctx.lineWidth = 2.5
        ctx.lineCap = 'round'
        // 感叹号竖线（居中）
        const exclamationTop = topY + triangleHeight * 0.25
        const exclamationBottom = topY + triangleHeight * 0.65
        ctx.beginPath()
        ctx.moveTo(centerX, exclamationTop)
        ctx.lineTo(centerX, exclamationBottom)
        ctx.stroke()
        // 感叹号点（居中）
        const dotY = topY + triangleHeight * 0.8
        ctx.beginPath()
        ctx.arc(centerX, dotY, 1.5, 0, Math.PI * 2)
        ctx.fillStyle = '#ff4444'
        ctx.fill()
        
        return canvas.toDataURL('image/png')
      },
      initAmap() {
        if (typeof window.AMap === 'undefined') {
          console.error('高德地图 API 未加载')
          return
        }
        
        const container = document.getElementById('alarm-amap-container')
        if (!container) {
          console.error('地图容器不存在')
          return
        }
        
        // 初始化高德地图 - 使用深色科技主题
        this.map = new window.AMap.Map('alarm-amap-container', {
          zoom: 17, // 初始缩放级别
          center: [114.0, 33.0], // 默认中心点，后续会根据配置更新
          mapStyle: 'amap://styles/darkblue', // 使用高德内置深蓝色主题，科技感强
          viewMode: '2D',
          features: ['bg', 'point', 'road', 'building']
        })
        
        // 地图加载完成后加载数据
        this.map.on('complete', () => {
          this.loadCommunityData()
        })
        
        // 绑定地图点击事件
        this.map.on('click', () => {
          this.closeAlarmPopup()
        })
      },
      updatePopupPosition() {
        if (!this.map || !this.selectedAlarmMarker) return
        const pos = this.selectedAlarmMarker.getPosition()
        if (!pos) {
          this.alarmPopupPos = { x: -9999, y: -9999 }
          return
        }
        const pixel = this.map.lngLatToContainer(pos)
        // 让弹窗出现在点位右上方（微偏移）
        this.alarmPopupPos = { x: Math.round(pixel.x + 12), y: Math.round(pixel.y - 150) }
      },
      openAlarmPopup(alarmData) {
        const statusMap = {
          unassigned: { text: '未派发', class: 'unassigned' },
          assigned: { text: '已派发', class: 'assigned' },
          cancelled: { text: '已取消', class: 'cancelled' }
        }
        const status = statusMap[alarmData.statusClass] || statusMap.unassigned

        this.alarmPopup = {
          title: alarmData.type || '告警信息',
          location: alarmData.location || '—',
          status: status.text,
          statusClass: status.class,
          rows: [
            [
              { k: '设备编号', v: alarmData.deviceId || '—' },
              { k: '设备名称', v: alarmData.deviceName || '—' }
            ],
            [
              { k: '告警时间', v: alarmData.time || '—' }
            ]
          ]
        }
        this.alarmPopupVisible = true
      },
      closeAlarmPopup() {
        this.alarmPopupVisible = false
        this.selectedAlarmMarker = null
        if (this.mapMoveHandler) {
          this.map.off('moveend', this.mapMoveHandler)
          this.map.off('zoomend', this.mapMoveHandler)
          this.mapMoveHandler = null
        }
      },
      // 检查点是否在多边形内（射线法）
      isPointInPolygon(point, polygon) {
        let inside = false
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i].longitude
          const yi = polygon[i].latitude
          const xj = polygon[j].longitude
          const yj = polygon[j].latitude
          const intersect = ((yi > point.lat) !== (yj > point.lat)) &&
            (point.lon < (xj - xi) * (point.lat - yi) / (yj - yi) + xi)
          if (intersect) inside = !inside
        }
        return inside
      },
      // 调整告警坐标，确保在边界内
      adjustAlarmCoordinatesToBoundary(boundary) {
        if (!boundary || boundary.length === 0) return

        // 计算边界范围
        const lons = boundary.map((p) => p.longitude)
        const lats = boundary.map((p) => p.latitude)
        const minLon = Math.min(...lons)
        const maxLon = Math.max(...lons)
        const minLat = Math.min(...lats)
        const maxLat = Math.max(...lats)
        const centerLon = (minLon + maxLon) / 2
        const centerLat = (minLat + maxLat) / 2

        // 检查并调整每个告警的坐标
        this.alarmList.forEach((alarm) => {
          const point = { lon: alarm.lon, lat: alarm.lat }

          // 检查是否在边界框内
          const inBoundingBox = alarm.lon >= minLon && alarm.lon <= maxLon &&
                                alarm.lat >= minLat && alarm.lat <= maxLat

          if (!inBoundingBox) {
            // 不在边界框内，调整到中心附近（确保在边界框内）
            alarm.lon = centerLon + (Math.random() - 0.5) * (maxLon - minLon) * 0.4
            alarm.lat = centerLat + (Math.random() - 0.5) * (maxLat - minLat) * 0.4
            // 确保在边界框内
            alarm.lon = Math.max(minLon, Math.min(maxLon, alarm.lon))
            alarm.lat = Math.max(minLat, Math.min(maxLat, alarm.lat))
          } else {
            // 在边界框内，检查是否在多边形内
            if (!this.isPointInPolygon(point, boundary)) {
              // 不在多边形内，尝试找到一个在多边形内的点
              // 使用多次随机尝试
              let found = false
              for (let i = 0; i < 10; i++) {
                const testLon = centerLon + (Math.random() - 0.5) * (maxLon - minLon) * 0.6
                const testLat = centerLat + (Math.random() - 0.5) * (maxLat - minLat) * 0.6
                if (this.isPointInPolygon({ lon: testLon, lat: testLat }, boundary)) {
                  alarm.lon = testLon
                  alarm.lat = testLat
                  found = true
                  break
                }
              }
              // 如果找不到，使用中心点（通常中心点会在多边形内）
              if (!found) {
                alarm.lon = centerLon
                alarm.lat = centerLat
              }
            }
          }
        })
      },
      loadCommunityData() {
        if (!this.map) return
        const cfg = getCommunityConfig(this.communityStore.currentCommunity)

        // 清除旧标记
        this.alarmMarkers.forEach((marker) => {
          this.map.remove(marker)
        })
        this.alarmMarkers = []
        if (this.boundaryPolyline) {
          this.map.remove(this.boundaryPolyline)
          this.boundaryPolyline = null
        }

        // 加载边界
        if (cfg && cfg.boundary && cfg.boundary.length > 0) {
          const path = cfg.boundary.map((p) => [p.longitude, p.latitude])
          this.boundaryPolyline = new window.AMap.Polyline({
            path: path,
            strokeColor: '#00d4ff',
            strokeWeight: 3,
            strokeOpacity: 0.8,
            zIndex: 1
          })
          this.map.add(this.boundaryPolyline)

          // 计算小区中心和边界范围（用于雷达扫描器映射）
          const lons = cfg.boundary.map((p) => p.longitude)
          const lats = cfg.boundary.map((p) => p.latitude)
          this.communityCenter.lon = (Math.min(...lons) + Math.max(...lons)) / 2
          this.communityCenter.lat = (Math.min(...lats) + Math.max(...lats)) / 2
          this.communityBounds.minLon = Math.min(...lons)
          this.communityBounds.maxLon = Math.max(...lons)
          this.communityBounds.minLat = Math.min(...lats)
          this.communityBounds.maxLat = Math.max(...lats)

          // 检查并调整默认告警坐标，确保在边界内
          this.adjustAlarmCoordinatesToBoundary(cfg.boundary)
        }

        // 加载告警点位
        this.alarmList.forEach((alarm) => {
          const iconUrl = this.getAlarmIcon()
          
          // 使用自定义 content 包含图标和标签，添加呼吸灯动画效果
          const markerContent = `
            <div class="alarm-marker-wrapper marker-alarm">
              <div class="marker-pulse"></div>
              <div class="alarm-marker-label">${alarm.type}</div>
              <img src="${iconUrl}" class="alarm-marker-icon" style="width: 32px; height: 32px;" />
            </div>
          `

          const marker = new window.AMap.Marker({
            position: [alarm.lon, alarm.lat],
            content: markerContent,
            title: alarm.type,
            anchor: 'bottom-center',
            extData: {
              alarmData: alarm
            }
          })

          // 绑定点击事件
          marker.on('click', () => {
            this.selectedAlarmMarker = marker
            this.openAlarmPopup(alarm)
            this.updatePopupPosition()
            // 监听地图移动和缩放，更新弹窗位置
            if (this.mapMoveHandler) {
              this.map.off('moveend', this.mapMoveHandler)
              this.map.off('zoomend', this.mapMoveHandler)
            }
            this.mapMoveHandler = () => {
              this.updatePopupPosition()
            }
            this.map.on('moveend', this.mapMoveHandler)
            this.map.on('zoomend', this.mapMoveHandler)
          })

          this.map.add(marker)
          this.alarmMarkers.push(marker)
        })

        // 初始过滤
        this.filterAlarmMarkers()

        // 清理雷达位置缓存，让位置重新计算
        if (window.scannedAlarmPositions) {
          window.scannedAlarmPositions.clear()
        }

        // 设置视角
        if (cfg && cfg.view) {
          this.flyToCommunity(cfg.view)
        }
      },
      flyToCommunity(view) {
        if (!this.map || !view) return
        
        // 优先使用边界范围
        if (this.boundaryPolyline) {
          const bounds = this.boundaryPolyline.getBounds()
          if (bounds) {
            // 设置合适的边距，确保能看到完整的边界线
            this.map.setBounds(bounds, false, [60, 60, 60, 60])
            // 限制缩放级别，稍微放大一点
            setTimeout(() => {
              const currentZoom = this.map.getZoom()
              // 如果缩放级别太大（>17），缩小到17
              if (currentZoom > 17) {
                this.map.setZoom(17)
              } else if (currentZoom < 15) {
                // 如果缩放级别太小，适当放大到15
                this.map.setZoom(15)
              }
            }, 100)
            return
          }
        }
        
        // 如果没有边界，使用配置的中心点和缩放级别
        let zoom = 17 // 默认缩放级别
        if (view.height) {
          // height 越大，缩放级别越小（地图越远）
          if (view.height > 2000) zoom = 16
          else if (view.height > 1000) zoom = 17
          else if (view.height > 500) zoom = 17
          else zoom = 17
        }
        
        this.map.setCenter([view.longitude || 114.0, view.latitude || 33.0])
        this.map.setZoom(zoom)
      },
      initTrendChart() {
        if (!this.$refs.trendChartRef) return
        if (this.trendChart) {
          this.trendChart.dispose()
        }
        this.trendChart = echarts.init(this.$refs.trendChartRef)
        const data = this.trendTimeRange === '7days'
          ? {
              dates: ['08/06', '08/07', '08/08', '08/09', '08/10', '08/11', '08/12'],
              events: [120, 132, 101, 134, 90, 130, 150],
              tickets: [80, 82, 91, 94, 90, 100, 110]
            }
          : {
              dates: ['02/06', '03/06', '04/06', '05/06', '06/06', '07/06', '08/06'],
              events: [150, 140, 160, 145, 155, 150, 145],
              tickets: [100, 95, 110, 105, 115, 110, 105]
            }
        this.trendChart.setOption({
          backgroundColor: 'transparent',
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(15, 30, 50, 0.95)',
            borderColor: 'rgba(0, 212, 255, 0.5)',
            borderWidth: 1,
            textStyle: { color: '#fff', fontSize: 12 },
            axisPointer: {
              type: 'cross',
              label: {
                backgroundColor: 'rgba(0, 212, 255, 0.3)'
              }
            }
          },
          legend: {
            data: ['事件', '工单'],
            textStyle: { color: '#a0b3c8', fontSize: 12 },
            bottom: 0,
            icon: 'roundRect',
            itemGap: 20
          },
          grid: { left: 45, right: 20, top: 35, bottom: 50 },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: data.dates,
            axisLine: { lineStyle: { color: 'rgba(255,255,255,0.2)' } },
            axisLabel: { color: '#a0b3c8', fontSize: 11 },
            axisTick: { show: false }
          },
          yAxis: {
            type: 'value',
            max: 200,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: '#a0b3c8', fontSize: 11 },
            splitLine: {
              lineStyle: {
                color: 'rgba(255,255,255,0.08)',
                type: 'dashed'
              }
            }
          },
          series: [
            {
              name: '事件',
              type: 'line',
              data: data.events,
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                color: '#ff9800',
                width: 3,
                shadowBlur: 10,
                shadowColor: 'rgba(255, 152, 0, 0.5)'
              },
              itemStyle: {
                color: '#ff9800',
                borderColor: '#fff',
                borderWidth: 2,
                shadowBlur: 8,
                shadowColor: 'rgba(255, 152, 0, 0.8)'
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: 'rgba(255, 152, 0, 0.4)' },
                  { offset: 1, color: 'rgba(255, 152, 0, 0.05)' }
                ])
              },
              emphasis: {
                focus: 'series',
                itemStyle: {
                  shadowBlur: 15,
                  shadowColor: 'rgba(255, 152, 0, 1)'
                }
              }
            },
            {
              name: '工单',
              type: 'line',
              data: data.tickets,
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                color: '#00d4ff',
                width: 3,
                shadowBlur: 10,
                shadowColor: 'rgba(0, 212, 255, 0.5)'
              },
              itemStyle: {
                color: '#00d4ff',
                borderColor: '#fff',
                borderWidth: 2,
                shadowBlur: 8,
                shadowColor: 'rgba(0, 212, 255, 0.8)'
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: 'rgba(0, 212, 255, 0.4)' },
                  { offset: 1, color: 'rgba(0, 212, 255, 0.05)' }
                ])
              },
              emphasis: {
                focus: 'series',
                itemStyle: {
                  shadowBlur: 15,
                  shadowColor: 'rgba(0, 212, 255, 1)'
                }
              }
            }
          ]
        })
      },
      initRankingChart() {
        if (!this.$refs.rankingChartRef) return
        if (this.rankingChart) {
          this.rankingChart.dispose()
        }
        this.rankingChart = echarts.init(this.$refs.rankingChartRef)
        const data = this.rankingTimeRange === '7days'
          ? [
              { name: '停车场', value: 105 },
              { name: '西大门', value: 70 },
              { name: '游乐场', value: 105 },
              { name: '中心湖区', value: 70 },
              { name: '篮球场', value: 105 },
              { name: '东边草地', value: 70 }
            ]
          : [
              { name: '停车场', value: 630 },
              { name: '西大门', value: 420 },
              { name: '游乐场', value: 630 },
              { name: '中心湖区', value: 420 },
              { name: '篮球场', value: 630 },
              { name: '东边草地', value: 420 }
            ]
        // 计算百分比
        const maxValue = Math.max(...data.map((d) => d.value))
        const dataWithPercent = data.map((d) => ({
          ...d,
          percent: Math.round((d.value / maxValue) * 100)
        }))

        this.rankingChart.setOption({
          backgroundColor: 'transparent',
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(15, 30, 50, 0.95)',
            borderColor: 'rgba(0, 212, 255, 0.5)',
            borderWidth: 1,
            textStyle: { color: '#fff', fontSize: 12 },
            formatter: (params) => {
              const param = params[0]
              const item = dataWithPercent.find((d) => d.name === param.name)
              return `${item.name}<br/>${param.value} 件 (${item.percent}%)`
            },
            axisPointer: {
              type: 'shadow',
              shadowStyle: {
                color: 'rgba(0, 212, 255, 0.2)'
              }
            }
          },
          grid: { left: 90, right: 60, top: 25, bottom: 35 },
          xAxis: {
            type: 'value',
            max: 700,
            name: '件',
            nameLocation: 'end',
            nameTextStyle: { color: '#a0b3c8', fontSize: 11, padding: [5, 0, 0, 0] },
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: '#a0b3c8', fontSize: 11 },
            splitLine: {
              lineStyle: {
                color: 'rgba(255,255,255,0.08)',
                type: 'dashed'
              },
              show: true
            }
          },
          yAxis: {
            type: 'category',
            data: data.map((d) => d.name),
            axisLine: { lineStyle: { color: 'rgba(255,255,255,0.2)' } },
            axisLabel: { color: '#a0b3c8', fontSize: 12 },
            axisTick: { show: false }
          },
          series: [
            {
              type: 'bar',
              barWidth: '60%',
              data: data.map((d, index) => {
                const colors = [
                  ['#00d4ff', '#00a8cc'],
                  ['#ff9800', '#cc7700'],
                  ['#00d4ff', '#00a8cc'],
                  ['#ff9800', '#cc7700'],
                  ['#00d4ff', '#00a8cc'],
                  ['#ff9800', '#cc7700']
                ]
                const colorPair = colors[index % colors.length]
                return {
                  value: d.value,
                  itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                      { offset: 0, color: colorPair[0] },
                      { offset: 1, color: colorPair[1] }
                    ]),
                    borderRadius: [0, 4, 4, 0],
                    shadowBlur: 8,
                    shadowColor: colorPair[0] + '80'
                  },
                  label: {
                    show: true,
                    position: 'right',
                    formatter: (params) => `${params.value} 件`,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 'bold',
                    textShadowColor: 'rgba(0, 0, 0, 0.8)',
                    textShadowBlur: 2
                  }
                }
              }),
              emphasis: {
                itemStyle: {
                  shadowBlur: 15,
                  shadowColor: 'rgba(0, 212, 255, 0.6)'
                }
              },
              animationDelay: (idx) => idx * 100
            }
          ],
          animationEasing: 'elasticOut'
        })
      },
      initRadarScanner() {
        if (!this.$refs.radarCanvasRef) return
        const canvas = this.$refs.radarCanvasRef
        const ctx = canvas.getContext('2d')
        const size = 150 // 雷达图大小
        canvas.width = size
        canvas.height = size

        const drawRadar = () => {
          ctx.clearRect(0, 0, size, size)
          const centerX = size / 2
          const centerY = size / 2
          const radius = size / 2 - 10

          // 绘制外圈边框
          ctx.strokeStyle = 'rgba(0, 212, 255, 0.6)'
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
          ctx.stroke()

          // 绘制网格（同心圆和径向线）
          ctx.strokeStyle = 'rgba(0, 212, 255, 0.4)'
          ctx.lineWidth = 1

          // 同心圆
          for (let i = 1; i <= 4; i++) {
            ctx.beginPath()
            ctx.arc(centerX, centerY, (radius * i) / 4, 0, Math.PI * 2)
            ctx.stroke()
          }

          // 径向线
          ctx.strokeStyle = 'rgba(0, 212, 255, 0.3)'
          for (let i = 0; i < 8; i++) {
            const angle = (Math.PI * 2 * i) / 8
            ctx.beginPath()
            ctx.moveTo(centerX, centerY)
            ctx.lineTo(
              centerX + Math.cos(angle) * radius,
              centerY + Math.sin(angle) * radius
            )
            ctx.stroke()
          }

          // 绘制扫描扇形
          ctx.save()
          ctx.beginPath()
          ctx.moveTo(centerX, centerY)
          ctx.arc(centerX, centerY, radius, this.radarAngle - Math.PI / 6, this.radarAngle + Math.PI / 6)
          ctx.closePath()
          const gradient = ctx.createLinearGradient(centerX, centerY, centerX + Math.cos(this.radarAngle) * radius, centerY + Math.sin(this.radarAngle) * radius)
          gradient.addColorStop(0, 'rgba(0, 212, 255, 0.5)')
          gradient.addColorStop(0.5, 'rgba(0, 212, 255, 0.3)')
          gradient.addColorStop(1, 'rgba(0, 212, 255, 0)')
          ctx.fillStyle = gradient
          ctx.fill()

          // 扫描扇形的边缘线
          ctx.strokeStyle = 'rgba(0, 212, 255, 0.8)'
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.moveTo(centerX, centerY)
          ctx.lineTo(
            centerX + Math.cos(this.radarAngle - Math.PI / 6) * radius,
            centerY + Math.sin(this.radarAngle - Math.PI / 6) * radius
          )
          ctx.stroke()
          ctx.beginPath()
          ctx.moveTo(centerX, centerY)
          ctx.lineTo(
            centerX + Math.cos(this.radarAngle + Math.PI / 6) * radius,
            centerY + Math.sin(this.radarAngle + Math.PI / 6) * radius
          )
          ctx.stroke()
          ctx.restore()

          // 绘制中心点
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
          ctx.beginPath()
          ctx.arc(centerX, centerY, 3, 0, Math.PI * 2)
          ctx.fill()

          // 获取当前显示的告警实体（根据过滤条件）
          const visibleAlarms = this.alarmList.filter((alarm) => {
            if (!this.selectedAlarmType) return true
            return alarm.type === this.selectedAlarmType
          })

          // 存储已扫描到的告警位置（持续显示一段时间）
          if (!window.scannedAlarmPositions) {
            window.scannedAlarmPositions = new Map()
          }

          // 只有在有可见告警时才绘制目标点
          if (visibleAlarms.length > 0) {
            // 计算小区的经纬度范围
            const lonRange = this.communityBounds.maxLon - this.communityBounds.minLon
            const latRange = this.communityBounds.maxLat - this.communityBounds.minLat

            visibleAlarms.forEach((alarm) => {
              // 使用告警的deviceId和经纬度生成稳定的位置
              const posKey = `${alarm.deviceId}-${alarm.lon}-${alarm.lat}`

              let alarmPos = window.scannedAlarmPositions.get(posKey)

              // 如果没有缓存的位置，根据经纬度生成一个稳定的位置
              if (!alarmPos) {
                // 计算告警相对于小区中心的位置
                const lonDiff = alarm.lon - this.communityCenter.lon
                const latDiff = alarm.lat - this.communityCenter.lat

                // 计算实际距离（单位：度）
                const actualDistance = Math.sqrt(lonDiff * lonDiff + latDiff * latDiff)

                // 转换为角度
                let angle = Math.PI / 2 - Math.atan2(latDiff, lonDiff)
                // 确保角度在 0 到 2π 范围内
                while (angle < 0) {
                  angle += Math.PI * 2
                }
                while (angle >= Math.PI * 2) {
                  angle -= Math.PI * 2
                }

                // 计算归一化距离（相对于小区边界范围）
                const maxDiagonalDistance = Math.sqrt(lonRange * lonRange + latRange * latRange) || 0.01
                const normalizedDist = Math.min(actualDistance / maxDiagonalDistance, 1)

                // 将距离映射到雷达的半径范围内
                const minRadius = 25 // 最小半径
                const maxRadius = radius - 15 // 最大半径
                const distance = minRadius + normalizedDist * (maxRadius - minRadius)

                // 计算在雷达上的坐标
                const x = centerX + Math.sin(angle) * distance
                const y = centerY - Math.cos(angle) * distance

                alarmPos = {
                  angle,
                  distance,
                  x,
                  y
                }

                window.scannedAlarmPositions.set(posKey, alarmPos)
              }

              const { x, y, angle: targetAngle } = alarmPos

              // 检查是否在扫描范围内
              let angleDiff = Math.abs(targetAngle - this.radarAngle)
              // 处理角度环绕
              if (angleDiff > Math.PI) {
                angleDiff = Math.PI * 2 - angleDiff
              }
              // 扫描扇形宽度为 π/3（60度），所以检查 ±π/6（30度）范围内
              const isScanned = angleDiff < Math.PI / 6

              // 如果扫描到目标，标记为已扫描（持续显示3秒）
              if (isScanned) {
                window.scannedAlarmPositions.set(posKey + '_scanned', Date.now())
              }

              // 检查是否应该显示（刚刚被扫描过，或正在被扫描）
              const scannedTime = window.scannedAlarmPositions.get(posKey + '_scanned')
              const shouldShow = isScanned || (scannedTime && Date.now() - scannedTime < 3000)

              // 只有当扫描到目标或刚刚扫描过时才显示红点
              if (shouldShow) {
                const alpha = isScanned ? 1 : 0.6 // 正在扫描时更亮
                ctx.fillStyle = `rgba(255, 68, 68, ${alpha})`
                ctx.beginPath()
                ctx.arc(x, y, isScanned ? 4 : 3, 0, Math.PI * 2)
                ctx.fill()

                // 扫描时的发光效果
                if (isScanned) {
                  ctx.shadowBlur = 10
                  ctx.shadowColor = '#ff4444'
                  ctx.beginPath()
                  ctx.arc(x, y, 5, 0, Math.PI * 2)
                  ctx.fill()
                  ctx.shadowBlur = 0
                }
              }
            })

            // 清理过期的扫描记录
            const now = Date.now()
            for (const [key, time] of window.scannedAlarmPositions.entries()) {
              if (key.endsWith('_scanned') && now - time > 3000) {
                window.scannedAlarmPositions.delete(key)
              }
            }
          }

          // 更新扫描角度
          this.radarAngle += 0.02
          if (this.radarAngle > Math.PI * 2) {
            this.radarAngle = 0
          }

          this.radarAnimationFrame = requestAnimationFrame(drawRadar)
        }

        drawRadar()
      },
      showDetails() {
        // TODO: 弹窗显示详情
      }
    }
  }
</script>

<style lang="scss" scoped>
.alarm-center {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

// ========== 左侧面板（覆盖在地图上，响应式）==========
.alarm-left-panel-wrapper {
  position: absolute;
  top: 20px;
  left: 20px;
  width: min(400px, 20vw); // 自适应：最大400px，或屏幕宽度的20%
  min-width: 300px; // 最小宽度
  max-width: 400px; // 最大宽度
  height: calc(100% - 40px);
  z-index: 10; // 左侧面板在地图上方
  border-radius: 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgba(15, 30, 50, 0.85);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  
  // 响应式调整
  @media (max-width: 1920px) {
    width: min(380px, 20vw);
    max-width: 380px;
  }
  
  @media (max-width: 1600px) {
    width: min(350px, 22vw);
    max-width: 350px;
    min-width: 280px;
  }
  
  @media (max-width: 1366px) {
    width: min(320px, 24vw);
    max-width: 320px;
    min-width: 260px;
  }
}

.alarm-left-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1; // 占据剩余空间
  min-height: 0; // 重要：允许 flex 子元素缩小
  overflow-y: auto; // 内容超出时显示滚动条
  overflow-x: hidden;
  max-height: 100%; // 限制最大高度，确保可以滚动

  // 隐藏滚动条但保留滚动功能
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }

  ::v-deep .card {
    padding: 10px; // 与社区态势保持一致
  }

  ::v-deep .section-title {
    margin-bottom: 0; // 与社区态势保持一致
    font-size: 14px; // 与社区态势保持一致
  }

  // 所有卡片默认根据内容自适应
  .card {
    flex: 0 0 auto;
    min-height: 0; // 允许收缩
  }

  // 最后一个卡片占据剩余空间，并设置为 flex 容器
  .card:last-child {
    flex: 1 0 auto; // 占据剩余空间，但不收缩，允许根据内容增长
    display: flex;
    flex-direction: column;
    // 不设置 min-height，让内容自然增长
    // 不设置 overflow，让内容自然溢出，由父容器 .alarm-left-panel 处理滚动
  }
}

.section-trend {
  // flex 由 .card:last-child 控制（当它是最后一个时）
  min-height: 280px; // 设置最小高度，确保有足够空间显示图表
  display: flex;
  flex-direction: column;
  overflow: hidden; // 防止内容溢出
}

.section-ranking {
  // flex 由 .card:last-child 控制（当它是最后一个时）
  min-height: 180px; // 设置最小高度，确保有足够空间显示图表
  display: flex;
  flex-direction: column;
  overflow: hidden; // 防止内容溢出
}

.section-workorder {
  // flex 由 .card:last-child 控制（当它是最后一个时）
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.trend-controls,
.ranking-controls {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  justify-content: flex-end;
}

.time-btn {
  padding: 4px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(30, 40, 60, 0.8);
  color: #a0b3c8;
  border-radius: 12px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
  white-space: nowrap;
  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(40, 50, 70, 0.9);
  }
  &.active {
    background: #7A8BEF;
    border-color: #7A8BEF;
    color: #fff;
    font-weight: 500;
  }
}

.trend-chart {
  flex: 1;
  min-height: 170px; // 设置最小高度，确保图表能显示
  .chart-container {
    width: 100%;
    height: 100%;
    min-height: 170px; // 确保容器有最小高度
  }
}

.ranking-chart {
  flex: 1;
  min-height: 120px; // 设置最小高度，确保图表能显示（缩小尺寸）
  .chart-container {
    width: 100%;
    height: 100%;
    min-height: 120px; // 确保容器有最小高度（缩小尺寸）
  }
}

.workorder-content {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.workorder-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.workorder-stat {
  padding: 12px;
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  text-align: center;
  .stat-label {
    font-size: 12px;
    color: #a0b3c8;
    margin-bottom: 8px;
  }
  .stat-value {
    font-size: 24px;
    font-weight: bold;
    color: #00d4ff;
    margin-bottom: 8px;
    .stat-unit {
      font-size: 14px;
      font-weight: normal;
    }
  }
  .stat-badge {
    display: inline-block;
    padding: 4px 10px;
    background: rgba(0, 212, 255, 0.2);
    border-radius: 12px;
    font-size: 11px;
    color: #00d4ff;
  }
}

.workorder-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 6px;
  .status-label {
    font-size: 12px;
    color: #a0b3c8;
  }
  .status-value {
    font-size: 14px;
    font-weight: bold;
    &.cyan {
      color: #00d4ff;
    }
    &.red {
      color: #ff6b6b;
    }
  }
}

.satisfaction {
  .satisfaction-label {
    font-size: 12px;
    color: #a0b3c8;
    margin-bottom: 12px;
  }
  .satisfaction-bars {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .bar-item {
    display: flex;
    align-items: center;
    gap: 10px;
    .bar-label {
      font-size: 12px;
      color: #a0b3c8;
      min-width: 60px;
    }
    .bar-wrapper {
      flex: 1;
      height: 20px;
      background: rgba(0, 212, 255, 0.1);
      border-radius: 10px;
      overflow: hidden;
    }
    .bar-fill {
      height: 100%;
      border-radius: 10px;
    }
    .bar-value {
      font-size: 12px;
      color: #00d4ff;
      min-width: 40px;
      text-align: right;
    }
  }
}

// ========== 中央地图 ==========
// ========== 中央地图（底部背景层）==========
.alarm-center-panel {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 1; // 地图在底部
  overflow: hidden;
}

.amap-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  
  // 高德地图样式覆盖
  ::v-deep .amap-icon {
    img {
      position: static !important;
      width: 100% !important;
      height: 100% !important;
      display: block !important;
    }
  }
  
  // 自定义地图控件样式，匹配大屏科技主题
  ::v-deep .amap-controls {
    .amap-toolbar,
    .amap-scale,
    .amap-logo,
    .amap-copyright {
      background: rgba(15, 30, 50, 0.9) !important;
      border: 1px solid rgba(0, 212, 255, 0.3) !important;
      color: #00d4ff !important;
      backdrop-filter: blur(10px);
    }
  }
  
  ::v-deep .amap-marker-label {
    background: transparent !important;
    border: none !important;
  }
  
  // 告警标记样式
  ::v-deep .alarm-marker-wrapper {
    position: relative;
    display: inline-block;
    text-align: center;
    
    .marker-pulse {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #ff4444;
      opacity: 0;
      z-index: 1;
      animation: pulse 2s ease-out infinite;
    }
    
    .alarm-marker-label {
      position: absolute;
      bottom: 37px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, rgba(255, 68, 68, 0.9) 0%, rgba(204, 0, 0, 0.85) 100%);
      color: #fff;
      padding: 5px 10px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: bold;
      white-space: nowrap;
      border: 1px solid rgba(255, 68, 68, 0.6);
      backdrop-filter: blur(8px);
      box-shadow: 0 2px 8px rgba(255, 68, 68, 0.4), 0 0 12px rgba(255, 68, 68, 0.3);
      pointer-events: none;
      z-index: 3;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
      letter-spacing: 0.5px;
    }
    
    .alarm-marker-icon {
      position: relative;
      z-index: 2;
      display: block;
    }
  }
  
  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0.8;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.5);
      opacity: 0.4;
    }
    100% {
      transform: translate(-50%, -50%) scale(2);
      opacity: 0;
    }
  }
}

// 地图右下角雷达扫描器（调整位置，避免被右侧面板遮挡）
.radar-scanner {
  position: absolute;
  bottom: 20px;
  right: calc(min(430px, 22vw) + 30px); // 右侧面板宽度 + 间距
  z-index: 100;
  width: 150px;
  height: 150px;
  pointer-events: none;
  background: rgba(15, 30, 50, 0.85);
  border: 2px solid rgba(0, 212, 255, 0.6);
  border-radius: 50%;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.4), inset 0 0 30px rgba(0, 212, 255, 0.1);
  padding: 5px;
  box-sizing: border-box;
  
  // 响应式调整
  @media (max-width: 1920px) {
    right: calc(min(410px, 22vw) + 30px);
  }
  
  @media (max-width: 1600px) {
    right: calc(min(380px, 24vw) + 30px);
  }
  
  @media (max-width: 1366px) {
    right: calc(min(350px, 26vw) + 30px);
  }
}

.radar-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

// 地图正下方悬浮的告警事件列表包装器
.map-alarm-list-wrapper {
  position: absolute;
  bottom: 20px;
  left: calc(min(400px, 20vw) + 40px); // 左侧面板宽度 + 更大间距，响应式
  right: calc(min(430px, 22vw) + 40px); // 右侧面板宽度 + 更大间距，响应式
  z-index: 200; // 确保在地图和左右面板之上
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  pointer-events: auto; // 确保可以交互
  min-width: 400px; // 确保最小宽度
  
  // 响应式调整
  @media (max-width: 1920px) {
    left: calc(min(380px, 20vw) + 40px);
    right: calc(min(410px, 22vw) + 40px);
  }
  
  @media (max-width: 1600px) {
    left: calc(min(350px, 22vw) + 40px);
    right: calc(min(380px, 24vw) + 40px);
    min-width: 300px;
  }
  
  @media (max-width: 1366px) {
    left: calc(min(320px, 24vw) + 40px);
    right: calc(min(350px, 26vw) + 40px);
    min-width: 250px;
  }
}

// 展开按钮（当列表隐藏时显示）
.alarm-list-toggle-btn {
  background: rgba(15, 30, 50, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 4px;
  padding: 8px 16px;
  color: #00d4ff;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  .toggle-text {
    font-weight: 500;
    margin-right: 10px;
  }

  .toggle-icon-up {
    display: inline-block;
    line-height: 1;
    letter-spacing: -2px;
  }

  &:hover {
    background: rgba(15, 30, 50, 0.95);
    border-color: #00d4ff;
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.4);
  }
}

// 地图正下方悬浮的告警事件列表
.map-alarm-list {
  background: rgba(15, 30, 50, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  width: 100%;
  max-width: 1400px;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .alarm-list-header {
    padding: 12px 16px;
    border-bottom: 1px solid rgba(0, 212, 255, 0.2);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    user-select: none;

    .section-header {
      margin-bottom: 10px;
    }

    .section-title {
      margin-bottom: 0;
      font-size: 14px; // 与社区态势保持一致
      flex: 1;
    }

    .toggle-btn {
      background: transparent;
      border: none;
      color: #00d4ff;
      font-size: 14px;
      cursor: pointer;
      padding: 4px 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.3s ease;

      .toggle-icon-down {
        display: inline-block;
      }

      &:hover {
        opacity: 0.8;
      }
    }
  }

  .alarm-list-scroll {
    overflow-y: auto;
    flex: 1;
    padding: 8px 16px 16px;
    min-height: 0;

    // 隐藏滚动条但保留滚动功能
    scrollbar-width: none; // Firefox
    -ms-overflow-style: none; // IE/Edge

    &::-webkit-scrollbar {
      display: none; // Chrome/Safari
    }
  }
}

.map-today-alarms {
  position: absolute;
  top: 20px;
  right: calc(min(430px, 22vw) + 40px); // 右侧面板宽度 + 更大间距
  z-index: 100;
  background: rgba(15, 30, 50, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  min-width: 210px;
  overflow: hidden;
  
  // 响应式调整
  @media (max-width: 1920px) {
    right: calc(min(410px, 22vw) + 40px);
  }
  
  @media (max-width: 1600px) {
    right: calc(min(380px, 24vw) + 40px);
  }
  
  @media (max-width: 1366px) {
    right: calc(min(350px, 26vw) + 40px);
  }
}

.today-alarms-header {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 152, 0, 0.1) 100%);
  border-bottom: 2px solid rgba(255, 152, 0, 0.6);
  border-radius: 6px 6px 0 0;
  padding: 9px 13px;
  display: flex;
  align-items: center;
  gap: 7px;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 0;
    border-left: 11px solid rgba(255, 152, 0, 0.6);
    border-bottom: 7px solid transparent;
  }
  .header-icon {
    font-size: 17px;
  }
  .header-title {
    font-size: 14px;
    font-weight: bold;
    color: #fff;
  }
}

.map-today-alarms .today-alarms-list {
  padding: 9px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.map-today-alarms .today-alarm-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 11px;
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  .alarm-icon {
    font-size: 15px;
    flex-shrink: 0;
  }
  .alarm-type {
    flex: 1;
    font-size: 12px;
    color: #fff;
    font-weight: 500;
    min-width: 0;
  }
  .alarm-count {
    font-size: 12px;
    color: #00d4ff;
    font-weight: bold;
    flex-shrink: 0;
    margin-left: auto;
  }
  &:hover {
    background: rgba(0, 212, 255, 0.12);
    border-color: rgba(0, 212, 255, 0.4);
  }
  &.active {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
    box-shadow: 0 0 12px rgba(0, 212, 255, 0.4);
    .alarm-type {
      color: #00d4ff;
    }
  }
}

.alarm-popup {
  position: absolute;
  z-index: 200;
  background: rgba(15, 30, 50, 0.95);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  min-width: 280px;
  max-width: 320px;
  pointer-events: auto;
}

.popup-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.popup-body {
  padding: 16px;
}

.popup-content {
  .popup-head {
    font-size: 16px;
    font-weight: bold;
    color: #00d4ff;
    margin-bottom: 12px;
  }
  .popup-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
    margin-bottom: 8px;
    .kv {
      font-size: 13px;
      color: #a0b3c8;
      b {
        color: #fff;
        font-weight: 600;
      }
    }
  }
  .popup-address,
  .popup-status {
    font-size: 13px;
    color: #a0b3c8;
    margin-top: 8px;
    text-align: left; // 确保左对齐
    b {
      color: #fff;
      font-weight: 600;
    }
  }
  .popup-action-btn {
    margin-top: 12px;
    width: 100%;
    padding: 8px;
    background: #00d4ff;
    border: none;
    border-radius: 6px;
    color: #000;
    font-size: 13px;
    font-weight: bold;
    cursor: pointer;
    &:hover {
      background: #00b8e6;
    }
  }
}

// ========== 右侧面板（覆盖在地图上，响应式）==========
.alarm-right-panel-wrapper {
  position: absolute;
  top: 20px;
  right: 20px;
  width: min(430px, 22vw); // 自适应：最大430px，或屏幕宽度的22%
  min-width: 320px; // 最小宽度
  max-width: 430px; // 最大宽度
  height: calc(100% - 40px);
  z-index: 10; // 右侧面板在地图上方
  border-radius: 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgba(15, 30, 50, 0.85);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  
  // 响应式调整
  @media (max-width: 1920px) {
    width: min(410px, 22vw);
    max-width: 410px;
  }
  
  @media (max-width: 1600px) {
    width: min(380px, 24vw);
    max-width: 380px;
    min-width: 300px;
  }
  
  @media (max-width: 1366px) {
    width: min(350px, 26vw);
    max-width: 350px;
    min-width: 280px;
  }
}

.alarm-right-panel {
  display: flex;
  flex-direction: column;
  gap: 8px; // 与左侧保持一致
  flex: 1; // 占据剩余空间
  min-height: 0; // 重要：允许 flex 子元素缩小
  overflow-y: auto; // 内容超出时显示滚动条
  overflow-x: hidden;
  max-height: 100%; // 限制最大高度，确保可以滚动

  // 隐藏滚动条但保留滚动功能
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }

  ::v-deep .card {
    padding: 10px; // 与社区态势保持一致
  }

  ::v-deep .section-title {
    margin-bottom: 0; // 与社区态势保持一致
    font-size: 14px; // 与社区态势保持一致
  }

  // 所有卡片默认根据内容自适应
  .card {
    flex: 0 0 auto;
    min-height: 0; // 允许收缩
  }

  // 最后一个卡片占据剩余空间，并设置为 flex 容器
  .card:last-child {
    flex: 1 0 auto; // 占据剩余空间，但不收缩，允许根据内容增长
    display: flex;
    flex-direction: column;
    // 不设置 min-height，让内容自然增长
    // 不设置 overflow，让内容自然溢出，由父容器 .alarm-right-panel 处理滚动
  }
}

.alarm-right-panel .section-header {
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.alarm-right-panel .section-title {
  font-size: 14px;
  margin-bottom: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-title {
  font-size: 14px;
  margin-bottom: 0;

  .title-icon {
    font-size: 18px;
    margin-right: 8px;
  }
}

.section-alarm-list {
  flex: 1 1 0; // 占据剩余空间，但允许收缩，基础大小为0
  min-height: 0; // 允许收缩
  display: flex;
  flex-direction: column;
  overflow: hidden; // 防止内容溢出
}

.alarm-list-scroll {
  flex: 1;
  min-height: 0;
  padding-right: 2px;
  overflow-y: auto; // 内容超出时显示滚动条

  // 隐藏滚动条但保留滚动功能
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }
}

.alarm-table {
  .table-header {
    display: grid;
    grid-template-columns: 0.8fr 1.2fr 0.9fr 1.1fr 1.2fr 0.8fr 0.7fr;
    gap: 6px;
    padding: 10px 8px;
    background: rgba(0, 212, 255, 0.1);
    border-radius: 6px;
    margin-bottom: 8px;
    .th {
      font-size: 12px;
      font-weight: bold;
      color: #00d4ff;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .table-row {
    display: grid;
    grid-template-columns: 0.8fr 1.2fr 0.9fr 1.1fr 1.2fr 0.8fr 0.7fr;
    gap: 6px;
    padding: 10px 8px;
    border-radius: 4px;
    margin-bottom: 6px;
    font-size: 12px;
    color: #a0b3c8;
    background: rgba(0, 212, 255, 0.02);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(0, 212, 255, 0.08);
    }

    &.new-alarm {
      background: rgba(255, 152, 0, 0.15);
      border-left: 3px solid rgba(255, 152, 0, 0.8);
      animation: slideInFromTop 0.5s ease-out;

      &:hover {
        background: rgba(255, 152, 0, 0.2);
      }
    }

    .td {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
    }
  }
}

.status-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  &.unassigned {
    background: rgba(255, 107, 107, 0.2);
    color: #ff6b6b;
  }
  &.assigned {
    background: rgba(0, 212, 255, 0.2);
    color: #00d4ff;
  }
  &.cancelled {
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
  }
}

.detail-btn {
  padding: 4px 12px;
  background: #00d4ff;
  border: none;
  border-radius: 4px;
  color: #000;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #00b8e6;
  }
}

// 过渡动画 - Vue 2 使用 enter/leave，Vue 3 使用 enter-from/leave-to
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// 新消息动画
@keyframes slideInFromTop {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
